import React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import DownloadIcon from '@mui/icons-material/Download';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DropDown from './DropDown';

const FEEDBACK_TYPES = [
  { iref: 0, name: 'OK' },
  { iref: 1, name: 'Info' },
  { iref: 2, name: 'Warning' },
  { iref: 3, name: 'Error' },
  { iref: 4, name: 'Debug' },
];
const TYPE_OPTIONS = Object.fromEntries(FEEDBACK_TYPES.map(t => [String(t.iref), t.name]));

const buildModuleOptions = (modules = []) => {
  // DropDown expects an object map: { value: label }
  const opts = { "": "All" };
  (modules || []).forEach(m => {
    if (m !== null && m !== undefined && String(m).trim() !== "") {
      opts[String(m)] = String(m);
    }
  });
  return opts;
};

export default class FeedbackFilters extends React.Component {
  constructor(props){
    super(props);
    this.state = {
      fType: '',
      fLang: '',
      fModule: '',     // ✅ exact module now
      fCode: '',
      fKeyword: '',
      filtersCounter: 0,
    };
    this.queryData = {};
    this.keywordRef = React.createRef();
    this.keywordTimer = null;
  }

  setQueryKey(key, val){
    if (!key) return;
    if (val === '' || val === null || val === undefined) delete this.queryData[key];
    else this.queryData[key] = val;
    this.setState({ filtersCounter: Object.keys(this.queryData).length });
  }

  applyNow = () => {
    this.props.applyFilters && this.props.applyFilters(this.queryData);
  }

  resetFilters = () => {
    this.queryData = {};
    this.setState({ fType:'', fLang:'', fModule:'', fCode:'', fKeyword:'', filtersCounter:0 });
    if (this.keywordRef.current) this.keywordRef.current.value = '';
    this.applyNow();
  }

  handleKeywordChange = (e) => {
    const kw = e.target.value || '';
    this.setState({ fKeyword: kw });
    clearTimeout(this.keywordTimer);
    this.keywordTimer = setTimeout(() => {
      if (kw.length > 2) this.setQueryKey('search', kw);
      else this.setQueryKey('search', null);
      this.applyNow();
    }, 250);
  }

  render(){
    const {
      languages,
      modules = [],               // ✅ new prop from parent
      onAddToggle,
      addActive,
      onExportFiltered,
      onExportAll,
      resultsCount = 0
    } = this.props;

    const MODULE_OPTIONS = buildModuleOptions(modules);

    return (
      <Box sx={{
        display:'grid',
        gridTemplateColumns:'auto 1fr auto auto',
        gap:1,
        alignItems:'center',
        mb:1, p:1,
        background:'#214a80',
        borderRadius:'12px'
      }}>

        {/* Middle: filters */}
        <Box sx={{ display:'flex', flexWrap:'wrap', gap:1.5, alignItems:'center' }}>
          <TextField
            id="keyword"
            inputRef={this.keywordRef}
            label="Keywords"
            variant="standard"
            onChange={this.handleKeywordChange}
            inputProps={{ sx : { color: "#e3e3e5" } }}
            InputLabelProps={{ sx : { color: "#e3e3e5" } }}
          />

          <DropDown
            name="Type"
            value={String(this.state.fType)}
            doptions={TYPE_OPTIONS}
            hasIcons={false}
            minWidth={140}
            onChangeFunction={(v)=>{
              this.setState({ fType: v });
              if (v !== '' && !isNaN(Number(v))) this.setQueryKey('type', String(v));
              else this.setQueryKey('type', null);
              this.applyNow();
            }}
          />

          <DropDown
            name="Language"
            value={this.state.fLang}
            doptions={languages}
            hasIcons={true}
            minWidth={160}
            onChangeFunction={(v)=>{
              this.setState({ fLang: v });
              if (v) this.setQueryKey('language[iref]', v);
              else this.setQueryKey('language[iref]', null);
              this.applyNow();
            }}
          />

          {/* ✅ Module dropdown (exact match) */}
          <DropDown
            name="Module"
            value={this.state.fModule}
            doptions={MODULE_OPTIONS}
            hasIcons={false}
            minWidth={180}
            onChangeFunction={(v)=>{
              this.setState({ fModule: v });
              // exact module, not contains
              if (v) this.setQueryKey('module', v);
              else this.setQueryKey('module', null);

              // make sure old param is removed if previously used
              this.setQueryKey('moduleLike', null);

              this.applyNow();
            }}
          />

          <TextField
            id="codeLike"
            label="Code contains"
            variant="outlined"
            size="small"
            value={this.state.fCode}
            onChange={(e)=>{
              const val = e.target.value;
              this.setState({ fCode: val });
              this.setQueryKey('codeLike', val || null);
            }}
            onBlur={this.applyNow}
          />

          { this.state.filtersCounter > 0 &&
            <Tooltip title="Reset filters">
              <IconButton onClick={this.resetFilters} size="small">
                <Avatar alt="Reset" sx={{ width: 18, height: 18, color: "#99302e" }}>
                  <HighlightOffIcon fontSize="inherit"/>
                </Avatar>
              </IconButton>
            </Tooltip>
          }
        </Box>

        {/* Right: compact export icons */}
        <Box sx={{ display:'flex', gap:0.5 }}>
          <Tooltip title="Export filtered">
            <IconButton aria-label="export-filtered" onClick={onExportFiltered} size="small">
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export all">
            <IconButton aria-label="export-all" onClick={onExportAll} size="small">
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Far right: counters */}
        <Box sx={{ justifySelf:'end', color:'#e3e3e5', fontSize:12, opacity:0.9, display:'flex', gap:1 }}>
          <span>Filters: {this.state.filtersCounter}</span>
          <span>Results: {resultsCount}</span>
        </Box>
      </Box>
    );
  }
}